import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

export const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <h1 className="text-6xl text-[#0F9015] mb-6 font-heading">
            Personal Ticket Control Centre
          </h1>
          <div className="text-5xl text-[#084509] max-w-5xl mx-auto font-heading font-normal space-y-2">
            <p>Keep track of all your tickets to all your events here. At the entrance quickly present your ticket for scanning.</p>
            <p>Distribute tickets amongst your mates with ease. Keep track of who has paid you for their ticket.</p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="bg-white rounded-xl shadow-lg p-8 transform transition-transform border-2 border-[#0F9015]">
            <div className="text-[#0F9015] mb-6">
              <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>
            </div>
            <h3 className="text-2xl text-[#0F9015] mb-4 font-heading">Store All Your Tickets</h3>
            <p className="text-lg text-[#084509] leading-relaxed font-body">
              It doesn't matter where you bought them from, don't ask "where is my ticket?" at an entrance ever again.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 transform transition-transform border-2 border-[#0F9015]">
            <div className="text-[#0F9015] mb-6">
              <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-2xl text-[#0F9015] mb-4 font-heading">Buying Tickets for Friends?</h3>
            <p className="text-lg text-[#084509] leading-relaxed font-body">
              Cut down on the administration tasks it takes for you to get your friends inside the event together.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 transform transition-transform border-2 border-[#0F9015]">
            <div className="text-[#0F9015] mb-6">
              <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <h3 className="text-2xl text-[#0F9015] mb-4 font-heading">Track Payments</h3>
            <p className="text-lg text-[#084509] leading-relaxed font-body">
              You offered to organize your friends, have they all paid you back for their tickets yet?
            </p>
          </div>
        </div>

        <div className="bg-[#0F9015] rounded-2xl shadow-sm p-8 md:p-12 mb-16">
          <div className="md:flex items-center justify-between">
            <div className="mb-6 md:mb-0 md:mr-8">
              <h2 className="text-4xl text-white mb-4 font-heading">
                Ready to make digital tickets easy to manage?
              </h2>
              <p className="text-xl text-white font-body">
                Tired of "herding cats" when you buy the tickets for your friends?
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              {!isAuthenticated ? (
                <>
                  <button
                    onClick={() => navigate('/auth/sign-up')}
                    className="px-8 py-4 bg-[#C9FD05] text-black text-xl rounded-lg hover:opacity-90 transition-opacity font-body"
                  >
                    Sign Up
                  </button>
                  <button
                    onClick={() => navigate('/auth/sign-in')}
                    className="px-8 py-4 bg-white text-[#0F9015] text-xl rounded-lg hover:opacity-90 transition-opacity font-body"
                  >
                    Sign In
                  </button>
                </>
              ) : (
                <button
                  onClick={() => navigate('/tickets/bulk')}
                  className="px-8 py-4 bg-[#C9FD05] text-black text-xl rounded-lg hover:opacity-90 transition-opacity font-body"
                >
                  Create New Ticket
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <h3 className="text-4xl text-[#0F9015] mb-8 font-heading">
              Organizing Your Own Tickets
            </h3>
            <ul className="space-y-8">
              <li className="flex items-start">
                <svg className="w-10 h-10 text-[#0F9015] mr-6 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-2xl text-[#084509] font-body">Store all digital tickets in one easy to remember location.</p>
              </li>
              <li className="flex items-start">
                <svg className="w-10 h-10 text-[#0F9015] mr-6 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-2xl text-[#084509] font-body">Easily find and present your ticket at the venue entrance.</p>
              </li>
              <li className="flex items-start">
                <svg className="w-10 h-10 text-[#0F9015] mr-6 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-2xl text-[#084509] font-body">Download the ticket beforehand and avoid overwhelmed network issues at the venue.</p>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-4xl text-[#0F9015] mb-8 font-heading">
              Buying Tickets For a Group
            </h3>
            <ul className="space-y-8">
              <li className="flex items-start">
                <svg className="w-10 h-10 text-[#0F9015] mr-6 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-2xl text-[#084509] font-body">Remember who you allocated individual tickets to, send the ticket to your friend as many times as they lose it.</p>
              </li>
              <li className="flex items-start">
                <svg className="w-10 h-10 text-[#0F9015] mr-6 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-2xl text-[#084509] font-body">Record who has paid you back for each ticket.</p>
              </li>
              <li className="flex items-start">
                <svg className="w-10 h-10 text-[#0F9015] mr-6 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-2xl text-[#084509] font-body">You might even save money by buying tickets as a group.</p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}; 